import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const th = { padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg,#D84315,#F57C00)', color: '#fff', fontWeight: 600, fontSize: '0.8rem', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.45rem 0.75rem', fontSize: '0.875rem', borderBottom: '1px solid #e0e0e0', color: '#2c3e50' };
const inp = { padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.875rem' };
const btn = (color = 'primary') => ({
  padding: '0.4rem 0.9rem', border: 'none', borderRadius: 5, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
  background: color === 'primary' ? 'linear-gradient(135deg,#D84315,#F57C00)' : color === 'danger' ? '#e74c3c' : '#e0e0e0',
  color: color === 'secondary' ? '#333' : '#fff',
});

const ddWrap = { position: 'relative' };
const ddList = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
  background: '#fff', border: '1px solid #ccc', borderRadius: 5,
  maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};
const ddItem = { padding: '0.4rem 0.7rem', fontSize: '0.85rem', cursor: 'pointer', color: '#2c3e50' };

function SmartInput({ placeholder, value, onChange, onSelect, options, minWidth = 440 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = value.trim()
    ? options.filter(o => o.name.toLowerCase().includes(value.toLowerCase()))
    : options;

  return (
    <div ref={ref} style={{ ...ddWrap, minWidth }}>
      <input
        style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && filtered.length > 0 && (
        <div style={ddList}>
          {filtered.map(o => (
            <div
              key={o.id}
              style={ddItem}
              onMouseEnter={e => e.currentTarget.style.background = '#fef3ee'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => { onSelect(o); setOpen(false); }}
            >
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ItemProductLocations() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${user?.token}` };

  const [tags, setTags]           = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems]         = useState([]);
  const [products, setProducts]   = useState([]);

  const [filterLoc, setFilterLoc]   = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterName, setFilterName] = useState('');
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  const [form, setForm]           = useState({ location_id: '', tag_type: 'item', item_id: '', product_id: '' });
  const [itemInput, setItemInput] = useState('');
  const [prodInput, setProdInput] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/stock/locations`, { headers, params: { type: 'WAREHOUSE', active: 'true' } })
      .then(r => setLocations(r.data || [])).catch(() => {});
    fetchFormLOVs('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFormLOVs = (locId) => {
    const params = locId ? { exclude_location_id: locId } : {};
    axios.get(`${API_URL}/commonLOVs/lhitems/LH`, { headers, params })
      .then(r => setItems(r.data || []))
      .catch(err => console.error('fetchFormLOVs items error:', err.response?.data || err.message));
    axios.get(`${API_URL}/commonLOVs/lhproducts/LH`, { headers, params })
      .then(r => setProducts(r.data || []))
      .catch(err => console.error('fetchFormLOVs products error:', err.response?.data || err.message));
  };

  const fetchTags = (locId = filterLoc) => {
    const params = {};
    if (locId) params.location_id = locId;
    axios.get(`${API_URL}/stock/item-product-locations`, { headers, params })
      .then(r => setTags(r.data || [])).catch(() => setTags([]));
  };

  useEffect(() => { fetchTags(); }, [filterLoc]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchType = (type) => {
    setForm(p => ({ ...p, tag_type: type, item_id: '', product_id: '' }));
    setItemInput('');
    setProdInput('');
  };

  const handleAdd = async () => {
    setError(''); setSuccess('');
    const payload = {
      location_id: parseInt(form.location_id),
      item_id:    form.tag_type === 'item'    ? parseInt(form.item_id)    : null,
      product_id: form.tag_type === 'product' ? parseInt(form.product_id) : null,
    };
    if (!payload.location_id || (!payload.item_id && !payload.product_id)) {
      setError('Select a location and an item or product.'); return;
    }
    try {
      await axios.post(`${API_URL}/stock/item-product-locations`, payload, { headers });
      setSuccess('Tag added.');
      setForm(p => ({ ...p, item_id: '', product_id: '' }));
      setItemInput(''); setProdInput('');
      fetchTags();
      fetchFormLOVs(String(payload.location_id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add tag.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this tag?')) return;
    setError(''); setSuccess('');
    try {
      await axios.delete(`${API_URL}/stock/item-product-locations/${id}`, { headers });
      setSuccess('Tag removed.');
      fetchTags();
      fetchFormLOVs(form.location_id);
    } catch {
      setError('Failed to remove tag.');
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 900 }}>
      <h2 style={{ marginBottom: '1.25rem', fontSize: '1.25rem', color: '#2c3e50' }}>Item / Product Location Tags</h2>

      {error   && <div style={{ background: '#fee', color: '#c33', border: '1px solid #fcc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ background: '#efe', color: '#2a7a2a', border: '1px solid #cfc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{success}</div>}

      {/* Add tag */}
      <section style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.1rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: '#333' }}>Add Tag</h3>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>

          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 3, color: '#555' }}>Location</div>
            <select style={inp} value={form.location_id} onChange={e => {
              const locId = e.target.value;
              setForm(p => ({ ...p, location_id: locId, item_id: '', product_id: '' }));
              setItemInput(''); setProdInput('');
              fetchFormLOVs(locId);
            }}>
              <option value="">-- Select --</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 3, color: '#555' }}>Type</div>
            <select style={inp} value={form.tag_type} onChange={e => switchType(e.target.value)}>
              <option value="item">Item</option>
              <option value="product">Product</option>
            </select>
          </div>

          {form.tag_type === 'item' ? (
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 3, color: '#555' }}>Item</div>
              <SmartInput
                placeholder="Search item..."
                value={itemInput}
                options={items}
                onChange={v => { setItemInput(v); if (!v) setForm(p => ({ ...p, item_id: '' })); }}
                onSelect={o => { setItemInput(o.name); setForm(p => ({ ...p, item_id: o.id })); }}
              />
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 3, color: '#555' }}>Product</div>
              <SmartInput
                placeholder="Search product..."
                value={prodInput}
                options={products}
                onChange={v => { setProdInput(v); if (!v) setForm(p => ({ ...p, product_id: '' })); }}
                onSelect={o => { setProdInput(o.name); setForm(p => ({ ...p, product_id: o.id })); }}
              />
            </div>
          )}

          <button style={btn()} onClick={handleAdd}>Add Tag</button>
        </div>
      </section>

      {/* Filter + list */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <select style={inp} value={filterLoc} onChange={e => setFilterLoc(e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select style={inp} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          <option value="item">Item</option>
          <option value="product">Product</option>
        </select>
        <input
          style={{ ...inp, minWidth: 200 }}
          placeholder="Search name..."
          value={filterName}
          onChange={e => setFilterName(e.target.value)}
        />
        <button style={btn('secondary')} onClick={() => { setFilterLoc(''); setFilterType(''); setFilterName(''); }}>Reset</button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Location</th>
              <th style={th}>Type</th>
              <th style={th}>Name</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const visible = tags.filter(t =>
                (!filterType || t.tag_type === filterType) &&
                (!filterName || t.name?.toLowerCase().includes(filterName.toLowerCase()))
              );
              return visible.length === 0
                ? <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#999', padding: '1.5rem' }}>No tags found.</td></tr>
                : visible.map(t => (
                    <tr key={t.id}>
                      <td style={td}>{t.location_name}</td>
                      <td style={td}>{t.tag_type}</td>
                      <td style={td}>{t.name}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button style={btn('danger')} onClick={() => handleDelete(t.id)}>Remove</button>
                      </td>
                    </tr>
                  ));
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
